import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Model from 'flarum/common/Model';
import Discussion from 'flarum/common/models/Discussion';
import PostImageLightbox from './components/PostImageLightbox';

// Discussion.images() -> the discussion's own images, in order, resolved
// server-side (see champis-forum's `images` field on DiscussionResource in
// extend.php) - real at runtime, absent from flarum/core's own Discussion
// typings.

/**
 * Opens PostImageLightbox for any image inside a post's own body (fof/
 * upload's own image-preview template, see vendor/fof/upload/resources/
 * templates/image-preview.blade.php: a plain <img> wrapped in
 * <a class="FoFUpload--Upl-Image-Preview-Link" href="{full-size url}">,
 * href pointing at the full-size original, target="_blank" by default -
 * intercepted and replaced with the lightbox here instead of a new tab).
 *
 * A native, delegated click listener on the post's own root DOM node,
 * not a Mithril onclick prop - a post's own body is server-rendered HTML
 * injected via contentHtml/m.trust(), so individual <img>/<a> tags in it
 * are never Mithril-owned elements Flarum's own components could attach
 * a handler to directly. Attached once in oncreate (fires exactly once
 * per post DOM node, standard Mithril semantics) rather than onupdate -
 * delegation means it keeps working correctly even as a post's own inner
 * HTML changes later (e.g. after an edit), since the listener lives on
 * the stable ancestor, not the (re-rendered) image/link themselves.
 *
 * The lightbox opens in gallery mode (prev/next through every image in
 * the discussion) using Discussion.images() below - not a DOM scan of
 * the discussion's other posts, because most of them usually aren't in
 * the DOM at all: Flarum's PostStream only ever mounts a window of posts
 * around the current scroll position (see vendor/flarum/core's own
 * PostStream.js visibleStart/visibleEnd). The server resolves the full,
 * ordered list instead (see champis-forum's extend.php `images` field on
 * DiscussionResource), and the discussion is always already loaded with
 * it by the time a post's own image can be clicked (it's the discussion
 * page's own primary request, not something that needs a separate fetch
 * here).
 */
export default function showPostImagesInLightbox() {
  Object.defineProperty(Discussion.prototype, 'images', {
    value: Model.attribute('images')
  });

  // extend()'s own typing can't resolve a real parameter type for CommentPost
  // from a bare string target (and CommentPost's own core typings declare
  // oncreate's vnode as `any` regardless - see flarum/core's generated
  // CommentPost.d.ts) - cast once here instead of leaving the callback's own
  // params (or a repeated inline cast below) implicitly/explicitly `any`.
  extend('flarum/forum/components/CommentPost', 'oncreate', function (value, vnode) {
    const {
      dom,
      attrs
    } = vnode;

    // HTMLElement, not the bare `dom: Element` VnodeDOM itself declares:
    // @types/mithril augments the *global* Element interface to extend
    // Mithril.Vnode (see its own index.d.ts), which breaks
    // addEventListener()'s own overload resolution against a literal event
    // name like 'click' - confirmed directly ("'click' is not assignable to
    // keyof ElementEventMap"). HTMLElement isn't touched by that merge.
    dom.addEventListener('click', e => {
      const link = e.target.closest('.FoFUpload--Upl-Image-Preview-Link');
      if (!link) return;
      e.preventDefault();
      const discussionImages = attrs.post.discussion().images() || [];
      const index = discussionImages.findIndex(image => image.url === link.href);

      // Falls back to just the clicked image alone if the discussion's
      // own images list didn't come back with a match for it (shouldn't
      // normally happen - every image the lightbox can be opened from is
      // itself one of the discussion's own posts' images) - a working,
      // gallery-less lightbox beats a broken click. The caption isn't in
      // the discussion's own images() list in this fallback case either,
      // so it's read straight off the DOM instead - the <figcaption>
      // ImageCaptionFix adds (see migration/ImageCaptionFix.php) sits
      // right inside the same <figure> as this link, a sibling of it.
      const images = index >= 0 ? discussionImages : [{
        url: link.href,
        alt: link.querySelector('img')?.alt || '',
        caption: link.closest('figure')?.querySelector('figcaption')?.textContent || null
      }];
      app.modal.show(PostImageLightbox, {
        images,
        index: index >= 0 ? index : 0
      });
    });
  });
}